import { z } from 'zod';
import { organizationRefSchema } from './organizations';
import { pageSchema, timestampSchema } from './common';
import { costPhasingMethodSchema, currencyCodeSchema, minorAmountSchema } from './costing';

/**
 * Scheduling (architecture documentation's scheduling feature requirements,
 * "Scheduling Foundation" phase -- see docs/decisions.md for the full list of
 * scope decisions).
 *
 * A scheduled item is a `ScheduleTask`; it becomes a summary row purely by
 * having children, so there is no separate "summary" item type here. Shapes
 * mirror @dashboard/scheduling's engine types where they overlap -- this file
 * is the wire contract, the engine package is the calculation, and the two
 * are kept in sync by hand rather than sharing a type, since one is a pure
 * calculation library with no notion of an HTTP boundary.
 */

export const scheduleItemTypeSchema = z.enum(['task', 'milestone']);
export type ScheduleItemType = z.infer<typeof scheduleItemTypeSchema>;

export const schedulingStateSchema = z.enum(['scheduled', 'unscheduled', 'blocked']);
export type SchedulingState = z.infer<typeof schedulingStateSchema>;

export const publicationStateSchema = z.enum(['draft', 'published']);
export type PublicationState = z.infer<typeof publicationStateSchema>;

export const dependencyTypeSchema = z.enum([
  'finish_to_start',
  'start_to_start',
  'finish_to_finish',
]);
export type DependencyType = z.infer<typeof dependencyTypeSchema>;

/** Listed as a const array as well as a schema, matching every other enum in
 *  this package: a form that renders one option per status needs the members,
 *  and reaching into `schema.options` for them reads as a workaround. */
export const WORKFLOW_STATUSES = [
  'not_started',
  'ready',
  'in_progress',
  'blocked',
  'done',
] as const;
export const workflowStatusSchema = z.enum(WORKFLOW_STATUSES);
export type WorkflowStatus = (typeof WORKFLOW_STATUSES)[number];

export const scheduleParticipantRoleSchema = z.enum(['contributor', 'watcher']);

const isoDateSchema = z.string().date();

export const drivingConstraintSchema = z.discriminatedUnion('kind', [
  /* The task starts when its access window opens (contracts:
   * access-windows.ts). Its own kind rather than reusing `earliest_start`, so
   * the editor can say "this starts when its access window opens" instead of
   * pointing at a field the reader never filled in. */
  z.object({ kind: z.literal('access_window') }),
  z.object({ kind: z.literal('project_start') }),
  z.object({ kind: z.literal('earliest_start') }),
  z.object({
    kind: z.literal('predecessor'),
    taskId: z.string().uuid(),
    dependencyId: z.string().uuid(),
  }),
  z.object({ kind: z.literal('children_rollup') }),
]);
export type DrivingConstraint = z.infer<typeof drivingConstraintSchema>;

/**
 * Which promise `deadlineFloatDays` is measured against (scheduling:
 * critical-path.ts).
 *
 * The number on its own is not actionable: "-3 days" is a fact, "-3 days
 * against the Bridge Handover deadline on 2027-03-01" is something a person
 * can go and do something about. The same shape and the same reasoning that
 * put `drivingConstraint` beside `calculatedStart`.
 *
 * `project_finish` means nothing was promised downstream of this task, and the
 * deadline float is only the total float restated -- the case a reader is
 * better served by a blank than by a number duplicating the column beside it.
 */
export const bindingCommitmentSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('project_finish') }),
  /** A `deadline`, on this task or on a phase above it. */
  z.object({ kind: z.literal('deadline'), taskId: z.string().uuid(), date: isoDateSchema }),
  /** The close of an access window (contracts: access-windows.ts). */
  z.object({ kind: z.literal('access_window'), taskId: z.string().uuid(), date: isoDateSchema }),
]);
export type BindingCommitment = z.infer<typeof bindingCommitmentSchema>;

export const scheduleWarningSchema = z.object({
  code: z.string(),
  params: z.record(z.string(), z.unknown()).optional(),
  affectedTaskIds: z.array(z.string().uuid()),
});
export type ScheduleWarning = z.infer<typeof scheduleWarningSchema>;

/**
 * The one warning code read by something other than the engine that emits it.
 *
 * Named here rather than spelled out at each use because the schedule summary
 * counts it in SQL: a string literal buried in a query and another in a
 * renderer are two copies of a rule that drift silently, and the first symptom
 * is a dashboard counting zero overdue tasks forever.
 */
export const DEADLINE_MISSED_WARNING = 'schedule.deadline_missed';

/* -------------------------------------------------------------------------- */
/* Progress                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Progress (docs/schedule-progress-design.md).
 *
 * The shapes mirror @dashboard/scheduling's `progress.ts` the way the rest of
 * this file mirrors the engine: that package is the calculation and this is the
 * wire contract. `scheduling.test.ts` in @dashboard/api asserts the two unions
 * are the same members, which is how they are stopped from drifting without
 * making a pure calculation library depend on an HTTP boundary.
 */

export const progressSourceSchema = z.enum(['reported', 'rolled_up', 'cost']);
export type ProgressSource = z.infer<typeof progressSourceSchema>;

export const progressWeightBasisSchema = z.enum(['auto', 'cost', 'duration', 'equal', 'manual']);
export type ProgressWeightBasis = z.infer<typeof progressWeightBasisSchema>;

/** What `auto` settled on. Never `auto`: that is a request, not an answer. */
export const resolvedWeightBasisSchema = z.enum(['cost', 'duration', 'equal', 'manual']);
export type ResolvedWeightBasis = z.infer<typeof resolvedWeightBasisSchema>;

/**
 * Progress is carried in basis points end to end -- database, wire, and
 * renderer -- and turned into a percentage only where it is displayed.
 *
 * A weighted average of integers is not an integer, and a chain of roll-ups
 * that rounded to whole percent at each level would make a parent disagree
 * visibly with its own children. Percent on the wire would put that rounding
 * at the boundary instead of at the edge, which is the same bug one layer out.
 */
export const PROGRESS_COMPLETE = 10_000;

export const progressBasisPointsSchema = z.number().int().min(0).max(PROGRESS_COMPLETE);

/** Basis points as a display percentage, rounded once, at the edge. */
export function basisPointsToPercent(basisPoints: number): number {
  return Math.round(basisPoints / 100);
}

export function percentToBasisPoints(percent: number): number {
  return Math.round(percent * 100);
}

/**
 * Warning codes the progress pass emits, named here for the reason
 * `DEADLINE_MISSED_WARNING` is: a literal in a renderer and another in the
 * calculation are two copies of one rule, and they drift silently.
 */
export const PROGRESS_ROLLUP_DISAGREES_WARNING = 'schedule.progress_rollup_disagrees';
export const PROGRESS_COST_WEIGHT_OVERLAP_WARNING = 'schedule.progress_cost_weight_overlap';
export const PROGRESS_WEIGHT_FALLBACK_WARNING = 'schedule.progress_weight_fallback';
export const PROGRESS_SOURCE_UNAVAILABLE_WARNING = 'schedule.progress_source_unavailable';
export const PROGRESS_OVERRUN_WARNING = 'schedule.progress_overrun';
export const PROGRESS_COST_OVERSPENT_WARNING = 'schedule.progress_cost_overspent';

/** The prefix every code above shares, and the one place that knows it. */
const PROGRESS_WARNING_PREFIX = 'schedule.progress_';

/**
 * The engine's warnings and the progress pass's, in one array.
 *
 * Two passes write `ScheduleTask.warnings` and neither may erase the other's
 * codes: the engine rewrites the array on every recompute, and the rollup
 * rewrites it on every report. Each replaces only what it owns, decided here
 * rather than in either of them -- a second copy of this rule in the second
 * pass is how one of them comes to drop the other's warnings silently.
 */
export function mergeProgressWarnings(
  existing: readonly ScheduleWarning[],
  progress: readonly ScheduleWarning[],
): ScheduleWarning[] {
  return [
    ...existing.filter((warning) => !warning.code.startsWith(PROGRESS_WARNING_PREFIX)),
    ...progress,
  ];
}

/** The progress pass's own warnings out of a mixed array. */
export function progressWarningsOf(warnings: readonly ScheduleWarning[]): ScheduleWarning[] {
  return warnings.filter((warning) => warning.code.startsWith(PROGRESS_WARNING_PREFIX));
}

/**
 * How far off the plan a row is, computed on every read and never stored --
 * it is a function of today's date, and a stored copy would go stale at
 * midnight (design section 6).
 *
 * **Positive is behind**, in both measures, matching the sign the baseline
 * variance column already uses so one tone vocabulary serves both.
 */
export const scheduleProgressPlanSchema = z.object({
  /** The date the comparison was made as of. */
  asOf: isoDateSchema,
  plannedBasisPoints: z.number().int().min(0).max(PROGRESS_COMPLETE).nullable(),
  behindByBasisPoints: z.number().int().nullable(),
  /**
   * The gap measured along the plan's own curve, in working days. The number to
   * lead with: it is in the unit the float columns beside it already speak, so
   * "four days behind" can be read against "six days of float".
   */
  behindByWorkingDays: z.number().int().nullable(),
});
export type ScheduleProgressPlan = z.infer<typeof scheduleProgressPlanSchema>;

/**
 * The project's completion over time: planned against actual, one point per
 * reporting month.
 *
 * Deliberately shaped like `CostTimeseries` and keyed by the same `YYYY-MM`
 * period, because the two charts are read together -- the cost page's S-curve
 * is money over time and this is work over time, and a reader comparing them
 * should not first have to reconcile two x-axes.
 */
export const scheduleProgressCurvePointSchema = z.object({
  /** `YYYY-MM`, the same period key the cost series uses. */
  period: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
  plannedBasisPoints: progressBasisPointsSchema.nullable(),
  actualBasisPoints: progressBasisPointsSchema.nullable(),
});
export type ScheduleProgressCurvePoint = z.infer<typeof scheduleProgressCurvePointSchema>;

export const scheduleProgressCurveSchema = z.object({
  projectId: z.string().uuid(),
  /** The period containing today, so the client can draw the "now" rule. */
  currentPeriod: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
  points: z.array(scheduleProgressCurvePointSchema),
});
export type ScheduleProgressCurve = z.infer<typeof scheduleProgressCurveSchema>;

/** One statement by one person, about one task, on one date. */
export const scheduleProgressReportSchema = z.object({
  id: z.string().uuid(),
  taskId: z.string().uuid(),
  asOf: isoDateSchema,
  progressBasisPoints: progressBasisPointsSchema,
  note: z.string().nullable(),
  reportedByUserId: z.string().uuid().nullable(),
  createdAt: timestampSchema,
});
export type ScheduleProgressReport = z.infer<typeof scheduleProgressReportSchema>;

export const createScheduleProgressReportSchema = z
  .object({
    progressBasisPoints: progressBasisPointsSchema,
    /**
     * The date the report is *about*. Defaults to today on the server, which is
     * where "today" can be resolved against the project's own timezone.
     */
    asOf: isoDateSchema.optional(),
    note: z.string().max(2000).nullish(),
  })
  .strict();
export type CreateScheduleProgressReport = z.infer<typeof createScheduleProgressReportSchema>;

/**
 * Many rows at once, which is how progress is actually reported: thirty rows
 * every Friday by somebody who already knows all thirty numbers (design 10.3).
 * One `asOf` and one note for the batch, because they describe the reporting
 * occasion rather than any single row.
 */
export const batchScheduleProgressReportSchema = z
  .object({
    asOf: isoDateSchema.optional(),
    note: z.string().max(2000).nullish(),
    entries: z
      .array(
        z
          .object({
            taskId: z.string().uuid(),
            progressBasisPoints: progressBasisPointsSchema,
          })
          .strict(),
      )
      .min(1)
      .max(200),
  })
  .strict()
  .refine(
    (value) => new Set(value.entries.map((entry) => entry.taskId)).size === value.entries.length,
    { message: 'A task may appear only once in a batch', path: ['entries'] },
  );
export type BatchScheduleProgressReport = z.infer<typeof batchScheduleProgressReportSchema>;

/**
 * Changing *how* a row is measured, as opposed to reporting against it.
 *
 * A separate payload from the report because it is a separate act by a
 * separate person: reporting is the right of whoever does the work, while
 * deciding the measurement and the weighting is a planning decision and needs
 * `schedule.manage` (design section 13).
 */
export const updateScheduleProgressSettingsSchema = z
  .object({
    progressSource: progressSourceSchema.optional(),
    childWeightBasis: progressWeightBasisSchema.optional(),
    progressWeight: z.number().min(0).max(1_000_000).nullish(),
    version: z.number().int().nonnegative(),
  })
  .strict();
export type UpdateScheduleProgressSettings = z.infer<typeof updateScheduleProgressSettingsSchema>;

/* -------------------------------------------------------------------------- */
/* Calendar                                                                    */
/* -------------------------------------------------------------------------- */

export const scheduleCalendarSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  timezone: z.string().min(1).max(64),
  workingWeekdays: z.array(z.number().int().min(0).max(6)),
  holidays: z.array(isoDateSchema),
  /**
   * The weekday progress is reported on, 0 (Sunday) to 6 (Saturday), or null
   * for no reporting cycle at all -- which is the default and means no digest
   * is sent (docs/schedule-progress-design.md section 12).
   */
  progressReportingWeekday: z.number().int().min(0).max(6).nullable(),
  version: z.number().int().nonnegative(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type ScheduleCalendar = z.infer<typeof scheduleCalendarSchema>;

export const updateScheduleCalendarSchema = z
  .object({
    timezone: z.string().min(1).max(64).optional(),
    workingWeekdays: z.array(z.number().int().min(0).max(6)).min(1).max(7).optional(),
    holidays: z.array(isoDateSchema).max(366).optional(),
    progressReportingWeekday: z.number().int().min(0).max(6).nullish(),
    version: z.number().int().nonnegative(),
  })
  .strict();
export type UpdateScheduleCalendar = z.infer<typeof updateScheduleCalendarSchema>;

/* -------------------------------------------------------------------------- */
/* Task                                                                        */
/* -------------------------------------------------------------------------- */

export const scheduleTaskSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  parentId: z.string().uuid().nullable(),
  /** True when this row currently has children -- it renders as a summary row. */
  hasChildren: z.boolean(),
  orderIndex: z.number().int(),

  name: z.string(),
  description: z.string().nullable(),
  itemType: scheduleItemTypeSchema,
  category: z.string().nullable(),
  tags: z.array(z.string()),

  /**
   * The project divisions this task is filed under (contracts:
   * project-structure.ts). `stageId` names a lifecycle phase, `sectionId` a
   * part of the undertaking; the two are independent, and both are optional
   * because a task drafted before anybody drew the structure is an ordinary
   * task, not a broken one.
   */
  stageId: z.string().uuid().nullable(),
  sectionId: z.string().uuid().nullable(),

  /**
   * The access window this task has to fit inside, when it needs one.
   *
   * A scheduling input, unlike the two divisions above it: a confirmed window
   * pushes the task's start up to its opening and warns when the work runs past
   * its close, so assigning one moves dates and needs a change session like any
   * other date-moving edit.
   */
  accessWindowId: z.string().uuid().nullable(),

  ownerUserId: z.string().uuid().nullable(),
  /**
   * The organization doing the work (docs/organizations-design.md section 9).
   * A supplier's people see the tasks their organization is responsible for,
   * and nothing else of the schedule. Not a scheduling input: assigning it
   * moves no date, so it saves without a change session.
   */
  responsibleOrganization: organizationRefSchema.nullable(),
  contributorUserIds: z.array(z.string().uuid()),
  watcherUserIds: z.array(z.string().uuid()),

  durationDays: z.number().int().nonnegative().nullable(),
  earliestStart: isoDateSchema.nullable(),
  deadline: isoDateSchema.nullable(),
  boundedDurationDays: z.number().int().nonnegative().nullable(),

  calculatedStart: isoDateSchema.nullable(),
  calculatedFinish: isoDateSchema.nullable(),
  drivingConstraint: drivingConstraintSchema.nullable(),
  schedulingState: schedulingStateSchema,
  warnings: z.array(scheduleWarningSchema),
  errorCode: z.string().nullable(),

  /**
   * The backward pass: the latest this task could start and finish without
   * moving the project's own end, and the working days of slack between that
   * and its calculated start.
   *
   * Null on anything the engine could not place -- "no slack" and "no dates"
   * are different claims. Deadlines deliberately do not constrain these: a
   * deadline is a warning in this product, and letting one pull the late dates
   * in would turn float into "time until somebody's stated wish" and run the
   * critical path through whichever task had the most optimistic date typed
   * against it.
   */
  lateStart: isoDateSchema.nullable(),
  lateFinish: isoDateSchema.nullable(),
  totalFloatDays: z.number().int().nonnegative().nullable(),
  /** Zero float: every day this slips is a day the project slips. */
  isCritical: z.boolean(),

  /**
   * The same backward pass against the nearest *promised* date instead of the
   * project's end -- a deadline or an access-window close, on this task or on
   * a phase above it, reached through the dependency chain.
   *
   * Signed, unlike `totalFloatDays`: negative means the engine has already
   * placed this task late enough to break that promise, and by how many
   * working days. This is the number that answers "how long can this run
   * before it breaks something somebody agreed to", which is what most people
   * mean when they ask for float -- while `totalFloatDays` keeps answering
   * "what decides the end date", which is what the critical path must be
   * drawn from. Neither moves a task: the forward pass stays the sole author
   * of the dates.
   */
  deadlineLateStart: isoDateSchema.nullable(),
  deadlineLateFinish: isoDateSchema.nullable(),
  deadlineFloatDays: z.number().int().nullable(),
  bindingCommitment: bindingCommitmentSchema.nullable(),

  workflowStatus: workflowStatusSchema,
  publicationState: publicationStateSchema,
  /**
   * Whether moving this task needs its owner's agreement. Off by default: an
   * owner locks the dates they have committed to, and only those turn somebody
   * else's edit into a request rather than a notification.
   */
  isLocked: z.boolean(),

  /* -- Progress (docs/schedule-progress-design.md) ------------------------- */

  /** What was asked for. */
  progressSource: progressSourceSchema,
  childWeightBasis: progressWeightBasisSchema,
  /** An explicit weight inside this row's own sibling group, if one was set. */
  progressWeight: z.number().nullable(),

  /** What the latest report at or before today said. */
  reportedBasisPoints: progressBasisPointsSchema.nullable(),

  /**
   * The resolved number for this row, and what the children add up to.
   *
   * Both null rather than zero when nothing has been reported: "nothing has
   * been reported here" and "we have done none of it" are different claims, and
   * the column renders them differently.
   *
   * `childRollupBasisPoints` is carried even on a row that does not use it --
   * it is the reconciliation number a directly-measured parent shows beside its
   * own, because a judgement that cannot be compared with the evidence it
   * overrides is indistinguishable from a typo.
   */
  progressBasisPoints: progressBasisPointsSchema.nullable(),
  childRollupBasisPoints: progressBasisPointsSchema.nullable(),
  /**
   * What actually produced the number, and what the children were actually
   * weighted by -- which is not always what the row asked for, because a group
   * falls back together when one child cannot supply the basis.
   */
  progressResolved: progressSourceSchema.nullable(),
  weightBasisResolved: resolvedWeightBasisSchema.nullable(),
  progressUpdatedAt: timestampSchema.nullable(),

  /**
   * The money behind a `cost`-sourced number: the budget linked to this row's
   * subtree and what has been realized against it, both in minor units.
   *
   * Sent on every row rather than only the ones measured by money, because
   * they are also what a `cost`-weighted group is weighted by -- and because a
   * percentage labelled "financial proxy" with nothing beside it is a number a
   * reader has to take on trust. These are what turns it into one they can
   * check (design 5.2).
   */
  costBudgetMinor: minorAmountSchema,
  costActualMinor: minorAmountSchema,

  actualStart: isoDateSchema.nullable(),
  actualFinish: isoDateSchema.nullable(),

  /**
   * How far off the plan this row is today. Computed on every read and never
   * stored (design section 6), and null on a row with no dates or nothing
   * reported -- a zero there would say "on plan" about a comparison nobody
   * made.
   */
  progressPlan: scheduleProgressPlanSchema.nullable(),
  /**
   * Whether the reader may file a progress report against this row.
   *
   * Sent rather than derived, so a control enabled in the browser and refused
   * by the API is a state the code cannot reach -- the rule
   * `docs/cost-editing-design.md` section 2 states, applied to the one field
   * whose write is scoped per row rather than per project.
   */
  canReportProgress: z.boolean(),

  version: z.number().int().nonnegative(),
  createdByUserId: z.string().uuid().nullable(),
  updatedByUserId: z.string().uuid().nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type ScheduleTask = z.infer<typeof scheduleTaskSchema>;

export const scheduleTaskPageSchema = pageSchema(scheduleTaskSchema);

function refineMilestoneDuration<T extends z.ZodTypeAny>(schema: T) {
  return schema.refine(
    (value) => {
      const record = value as { itemType?: ScheduleItemType; durationDays?: number | null };
      if (record.itemType !== 'milestone') return true;
      return record.durationDays == null || record.durationDays === 0;
    },
    { message: 'A milestone must have zero duration', path: ['durationDays'] },
  );
}

export const createScheduleTaskSchema = refineMilestoneDuration(
  z
    .object({
      name: z.string().min(1).max(200),
      description: z.string().max(4000).nullish(),
      itemType: scheduleItemTypeSchema.default('task'),
      category: z.string().max(120).nullish(),
      tags: z.array(z.string().min(1).max(64)).max(20).default([]),
      stageId: z.string().uuid().nullish(),
      sectionId: z.string().uuid().nullish(),
      accessWindowId: z.string().uuid().nullish(),
      parentId: z.string().uuid().nullish(),
      orderIndex: z.number().int().default(0),
      ownerUserId: z.string().uuid().nullish(),
      responsiblePartyId: z.string().uuid().nullish(),
      contributorUserIds: z.array(z.string().uuid()).max(50).default([]),
      watcherUserIds: z.array(z.string().uuid()).max(50).default([]),
      durationDays: z.number().int().min(0).max(3650).nullish(),
      earliestStart: isoDateSchema.nullish(),
      deadline: isoDateSchema.nullish(),
      boundedDurationDays: z.number().int().min(0).max(3650).nullish(),
      workflowStatus: workflowStatusSchema.default('not_started'),
      isLocked: z.boolean().default(false),
    })
    .strict(),
);
export type CreateScheduleTask = z.infer<typeof createScheduleTaskSchema>;

/**
 * The editable fields of a task, every one optional.
 *
 * Shared by the two ways an edit can arrive: applied straight away
 * (`updateScheduleTaskSchema`, which adds the optimistic-lock version), or
 * staged in a change session as a proposal (`scheduleTaskPatchSchema`). They
 * are one definition because they must accept exactly the same edits -- a
 * field that could be changed on one path and not the other would make the
 * session an incomplete substitute for editing, which is the one thing it
 * cannot be.
 */
/**
 * The writable fields that can move a calculated date.
 *
 * One definition, three consumers: the API skips its recompute (and its impact
 * analysis) for a patch that touches none of them, the API requires an open
 * change session for a patch that touches any of them, and the task editor
 * disables exactly these controls until that session exists. Written out here
 * rather than in any one of the three, because a field that is a scheduling
 * input on the server and an ordinary field in the editor is a hole in the
 * rule, not a cosmetic difference.
 *
 * `parentId` is one: a task's parent summarises it, so re-parenting moves two
 * rollups. `itemType` is one because turning a task into a milestone zeroes
 * its duration.
 */
export const SCHEDULING_INPUT_FIELDS = [
  'durationDays',
  'earliestStart',
  'deadline',
  'boundedDurationDays',
  'parentId',
  'itemType',
  /* Assigning a confirmed access window pushes the task's start up to the
   * window's opening, so it moves dates exactly as an earliest-start does.
   * Filing under a stage or a section does not, which is why those two are
   * deliberately absent from this list. */
  'accessWindowId',
] as const;
export type SchedulingInputField = (typeof SCHEDULING_INPUT_FIELDS)[number];

const SCHEDULING_INPUT_FIELD_SET: ReadonlySet<string> = new Set(SCHEDULING_INPUT_FIELDS);

export function isSchedulingInputField(field: string): field is SchedulingInputField {
  return SCHEDULING_INPUT_FIELD_SET.has(field);
}

/** Whether a patch would change any scheduling input, and so needs a session. */
export function touchesSchedulingInputs(patch: Record<string, unknown>): boolean {
  return SCHEDULING_INPUT_FIELDS.some((field) => patch[field] !== undefined);
}

const scheduleTaskWritableFields = z.object({
  name: z.string().min(1).max(200).optional(),
  description: z.string().max(4000).nullish(),
  itemType: scheduleItemTypeSchema.optional(),
  category: z.string().max(120).nullish(),
  tags: z.array(z.string().min(1).max(64)).max(20).optional(),
  stageId: z.string().uuid().nullish(),
  sectionId: z.string().uuid().nullish(),
  accessWindowId: z.string().uuid().nullish(),
  parentId: z.string().uuid().nullish(),
  orderIndex: z.number().int().optional(),
  ownerUserId: z.string().uuid().nullish(),
  responsiblePartyId: z.string().uuid().nullish(),
  contributorUserIds: z.array(z.string().uuid()).max(50).optional(),
  watcherUserIds: z.array(z.string().uuid()).max(50).optional(),
  durationDays: z.number().int().min(0).max(3650).nullish(),
  earliestStart: isoDateSchema.nullish(),
  deadline: isoDateSchema.nullish(),
  boundedDurationDays: z.number().int().min(0).max(3650).nullish(),
  workflowStatus: workflowStatusSchema.optional(),
  isLocked: z.boolean().optional(),
});

export const updateScheduleTaskSchema = refineMilestoneDuration(
  scheduleTaskWritableFields
    .extend({
      /** Value last read by the client; drift yields 412. */
      version: z.number().int().nonnegative(),
    })
    .strict(),
);
export type UpdateScheduleTask = z.infer<typeof updateScheduleTaskSchema>;

/** One task's proposed edit inside a change session. */
export const scheduleTaskPatchSchema = refineMilestoneDuration(
  scheduleTaskWritableFields
    .extend({
      /**
       * The task version the proposal was written against. Checked again at
       * confirmation, so a session that sat open while somebody else edited
       * the same task is reported rather than silently overwriting them
       * (requirements document section 8.7).
       */
      version: z.number().int().nonnegative(),
    })
    .strict(),
);
export type ScheduleTaskPatch = z.infer<typeof scheduleTaskPatchSchema>;

export const publishScheduleTaskSchema = z
  .object({ version: z.number().int().nonnegative() })
  .strict();
export type PublishScheduleTask = z.infer<typeof publishScheduleTaskSchema>;

export const updateScheduleTaskStatusSchema = z
  .object({ workflowStatus: workflowStatusSchema, version: z.number().int().nonnegative() })
  .strict();
export type UpdateScheduleTaskStatus = z.infer<typeof updateScheduleTaskStatusSchema>;

export const scheduleTaskListQuerySchema = z.object({
  ownerUserId: z.string().uuid().optional(),
  /** One organization's work. */
  responsiblePartyId: z.string().uuid().optional(),
  /**
   * Filing filters. `none` is a value, not the absence of one: "which work has
   * nobody assigned a stage to" is the question that closes the gap the
   * structure summary reports, and an omitted parameter cannot ask it.
   */
  stageId: z.union([z.string().uuid(), z.literal('none')]).optional(),
  sectionId: z.union([z.string().uuid(), z.literal('none')]).optional(),
  accessWindowId: z.union([z.string().uuid(), z.literal('none')]).optional(),
  /** Only the work that decides the end date. */
  criticalOnly: z.coerce.boolean().optional(),
  workflowStatus: workflowStatusSchema.optional(),
  hasWarnings: z.coerce.boolean().optional(),
  search: z.string().max(120).optional(),
  cursor: z.string().max(256).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(100),
});
export type ScheduleTaskListQuery = z.infer<typeof scheduleTaskListQuerySchema>;

/**
 * The four numbers the schedule dashboard tile shows.
 *
 * Its own endpoint rather than a page of tasks the browser counts, for the
 * reason `registerSummarySchema` gives: a tile that downloads every task in
 * order to count them gets slower as the project it reports on gets bigger,
 * and it was already capped at the largest page the API offers -- so past two
 * hundred tasks it was not merely slow, it was wrong.
 *
 * Counted over leaf tasks only. A parent's dates are derived from its children,
 * so counting both would report every piece of work twice and call a summary
 * task "overdue" for a reason its child already accounts for.
 */
export const scheduleSummarySchema = z.object({
  projectId: z.string().uuid(),
  /** Leaf tasks in the project: the denominator for the other three. */
  total: z.number().int().nonnegative(),
  /** Leaves the engine could not place, in any state other than `scheduled`. */
  blocked: z.number().int().nonnegative(),
  /** Leaves carrying at least one engine warning. */
  warned: z.number().int().nonnegative(),
  /** Leaves whose warnings include `schedule.deadline_missed`. */
  overdue: z.number().int().nonnegative(),
  /**
   * Leaves with no float at all -- the chain that decides the end date.
   *
   * Counted beside the other three because "how much of this project has no
   * slack left" is the number that turns the critical path from a colour on a
   * chart into something a reader can watch move.
   */
  critical: z.number().int().nonnegative(),
  /**
   * Leaves whose `deadlineFloatDays` has gone negative: work already placed
   * late enough to break a promise somebody made.
   *
   * Distinct from `overdue`, which counts the tasks *carrying* a missed date.
   * A predecessor three steps upstream of a missed milestone is the task that
   * has to move, and it appears here and not there -- which is the whole
   * reason the second float exists.
   */
  atRisk: z.number().int().nonnegative(),

  /**
   * The project's own completion, and how far off the plan it is today.
   *
   * The roots' roll-up rather than a count, so it is the same number the tree
   * shows and not a second one derived a second way. Null until somebody has
   * reported something, which is what keeps a project that has not been asked
   * from claiming it is 0% done.
   */
  progressBasisPoints: progressBasisPointsSchema.nullable(),
  progressPlan: scheduleProgressPlanSchema.nullable(),
  /** Leaves with progress reported against them: the denominator's companion. */
  reported: z.number().int().nonnegative(),
});
export type ScheduleSummary = z.infer<typeof scheduleSummarySchema>;

/* -------------------------------------------------------------------------- */
/* The link to cost (docs/schedule-progress-design.md section 5)               */
/* -------------------------------------------------------------------------- */

/**
 * One budget line, as the schedule sees it.
 *
 * The same row the cost page edits, described in the terms a planner reads it
 * in: which account it sits on, what it is worth, which task it belongs to,
 * and what has been spent against it. It is a *view* of `CostBudgetLine` and
 * deliberately not that type -- the phasing, the contingency flag and the
 * per-period amounts are the cost page's business, and a planner choosing
 * which work a budget belongs to does not need to read them.
 */
export const scheduleCostLinkSchema = z.object({
  budgetLineId: z.string().uuid(),
  costAccountId: z.string().uuid(),
  /** The account's own code and name, so the picker reads as a chart of accounts. */
  accountCode: z.string(),
  accountName: z.string(),
  /** The line's own label, where somebody gave it one. */
  name: z.string().nullable(),
  amountMinor: minorAmountSchema,
  /**
   * Whether this line's account holds budget for more than one task.
   *
   * No per-line spend is sent, because there is no such fact: an invoice is
   * posted against an *account*, and a line's "own" spend could only ever be a
   * share of one. The task's `costActualMinor` is where that share is
   * accounted for, once, and this flag is what tells the reader it was a share
   * -- an approximate figure that does not say so is the worse kind of wrong.
   */
  accountIsShared: z.boolean(),
  /** Null while the line belongs to no particular piece of work. */
  scheduleTaskId: z.string().uuid().nullable(),
  /** Resolved for the picker, so a line already spoken for says by whom. */
  scheduleTaskName: z.string().nullable(),
  phasingMethod: costPhasingMethodSchema,
});
export type ScheduleCostLink = z.infer<typeof scheduleCostLinkSchema>;

/**
 * Every budget line of the project's current budget, and which version that is.
 *
 * Project-scoped rather than per task: the task panel filters it, the picker
 * lists it, and one cached answer serves every row a planner opens. The
 * alternative -- a request per task -- would refetch the same catalogue thirty
 * times on a Friday afternoon.
 */
export const scheduleCostLinksSchema = z.object({
  projectId: z.string().uuid(),
  currency: currencyCodeSchema,
  /**
   * The budget these lines belong to. Null when the project has none at all,
   * which is the state the panel explains rather than showing an empty picker.
   */
  budgetVersionId: z.string().uuid().nullable(),
  budgetVersionLabel: z.string().nullable(),
  /**
   * Whether that budget is approved.
   *
   * It matters to the reader: only an approved budget feeds progress, so a
   * link made against a draft is real, saved, and doing nothing yet. Saying so
   * is the difference between a planner waiting and a planner filing a bug.
   */
  budgetVersionApproved: z.boolean(),
  /** Whether this reader may change the links. */
  canManage: z.boolean(),
  links: z.array(scheduleCostLinkSchema),
});
export type ScheduleCostLinks = z.infer<typeof scheduleCostLinksSchema>;

/**
 * Attaching a budget line to a task, moving it to another, or detaching it.
 *
 * All three are one write of one nullable column, so they are one request
 * rather than three routes. No `version`: the column carries no amount and no
 * date, two planners setting it are not in a race worth reporting, and the
 * last one to speak is right -- which is the honest outcome here and not a
 * concurrency hole.
 */
export const updateScheduleCostLinkSchema = z
  .object({ scheduleTaskId: z.string().uuid().nullable() })
  .strict();
export type UpdateScheduleCostLink = z.infer<typeof updateScheduleCostLinkSchema>;

/* -------------------------------------------------------------------------- */
/* Dependency                                                                  */
/* -------------------------------------------------------------------------- */

export const scheduleDependencySchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  predecessorId: z.string().uuid(),
  successorId: z.string().uuid(),
  dependencyType: dependencyTypeSchema,
  lagDays: z.number().int().nonnegative(),
  description: z.string().nullable(),
  createdByUserId: z.string().uuid().nullable(),
  createdAt: timestampSchema,
  version: z.number().int().nonnegative(),
});
export type ScheduleDependency = z.infer<typeof scheduleDependencySchema>;

export const createScheduleDependencySchema = z
  .object({
    predecessorId: z.string().uuid(),
    successorId: z.string().uuid(),
    dependencyType: dependencyTypeSchema.default('finish_to_start'),
    lagDays: z.number().int().min(0).max(3650).default(0),
    description: z.string().max(1000).nullish(),
  })
  .strict()
  .refine((value) => value.predecessorId !== value.successorId, {
    message: 'A task cannot depend on itself',
    path: ['successorId'],
  });
export type CreateScheduleDependency = z.infer<typeof createScheduleDependencySchema>;
